import React from 'react';
import SEO from '../components/SEO';

const COOKIE_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': 'https://phennx.io/cookie-policy#webpage',
      url: 'https://phennx.io/cookie-policy',
      name: 'Cookie Policy',
      description: 'Learn how Phennx uses cookies and how to manage your preferences.',
      isPartOf: { '@id': 'https://phennx.io/#website' },
      about: { '@id': 'https://phennx.io/#organization' },
    },
    {
      '@type': 'BreadcrumbList',
      '@id': 'https://phennx.io/cookie-policy#breadcrumbs',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://phennx.io/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Cookie Policy',
          item: 'https://phennx.io/cookie-policy',
        },
      ],
    },
  ],
};

export default function CookiePolicyPage() {
  return (
    <>
      <SEO
        title="Cookie Policy"
        description="Learn how Phennx uses cookies and how to manage your preferences."
        path="/cookie-policy"
        image="/logo-full-transparent.png"
        type="article"
        schema={COOKIE_SCHEMA}
      />
      <article className="legal-page" aria-label="Cookie Policy">
        <header className="legal-page__hero">
          <div className="section-inner">
            <div className="legal-page__eyebrow">Legal</div>
            <h1 className="legal-page__title">Cookie Policy</h1>
            <p className="legal-page__lede">
              We use cookies and similar technologies to provide, protect, and improve our website, analyze how it is
              used, and deliver relevant advertising. We do not sell your personal data.
            </p>
            <p className="legal-page__updated">Last updated: May 2, 2025</p>
          </div>
        </header>

        <section className="legal-page__content">
          <div className="section-inner">
            <div className="legal-page__section">
              <h2>What are cookies?</h2>
              <p>
                Cookies are small text files placed on your device when you visit websites. They help the site remember
                your actions and preferences over time so you do not have to re-enter them each time you visit.
              </p>
            </div>

            <div className="legal-page__section">
              <h2>Why we use cookies</h2>
              <ul className="legal-page__list">
                <li>Ensure the website functions properly, including navigation and secure areas.</li>
                <li>Remember your preferences, such as language or region.</li>
                <li>Analyze usage and performance.</li>
                <li>Deliver and measure the effectiveness of advertising.</li>
              </ul>
            </div>

            <div className="legal-page__section">
              <h2>Types of cookies we use</h2>
              <p>
                In addition to cookies, we may use other automatic data collection technologies such as web beacons or
                pixel tags.
              </p>
              <ul className="legal-page__list">
                <li>Strictly necessary cookies that enable core functionality and security.</li>
                <li>Performance and analytics cookies that help us improve how the site works.</li>
                <li>Functionality cookies that remember choices you make.</li>
                <li>Target and advertising cookies that help deliver relevant ads.</li>
              </ul>
            </div>

            <div className="legal-page__section">
              <h2>Third-party cookies and advertising platforms</h2>
              <p>
                We run or plan to run ads on the following platforms, which may place cookies or similar technologies
                on your device:
              </p>
              <ul className="legal-page__list">
                <li>Meta (Facebook and Instagram)</li>
                <li>Google (Search &amp; Display Network, YouTube)</li>
                <li>Twitter</li>
                <li>Pinterest</li>
                <li>TikTok</li>
              </ul>
              <p>For details on how each platform uses cookies and how to opt out, visit their privacy centers:</p>
              <ul className="legal-page__list">
                <li>
                  Meta:{' '}
                  <a href="https://www.facebook.com/policy.php" target="_blank" rel="noreferrer">
                    https://www.facebook.com/policy.php
                  </a>
                </li>
                <li>
                  Google:{' '}
                  <a href="https://policies.google.com/technologies/cookies" target="_blank" rel="noreferrer">
                    https://policies.google.com/technologies/cookies
                  </a>
                </li>
                <li>
                  Twitter:{' '}
                  <a href="https://help.twitter.com/en/rules-and-policies/twitter-cookies" target="_blank" rel="noreferrer">
                    https://help.twitter.com/en/rules-and-policies/twitter-cookies
                  </a>
                </li>
                <li>
                  Pinterest:{' '}
                  <a href="https://policy.pinterest.com/en/cookies" target="_blank" rel="noreferrer">
                    https://policy.pinterest.com/en/cookies
                  </a>
                </li>
                <li>
                  TikTok:{' '}
                  <a href="https://www.tiktok.com/legal/cookie-policy" target="_blank" rel="noreferrer">
                    https://www.tiktok.com/legal/cookie-policy
                  </a>
                </li>
              </ul>
            </div>

            <div className="legal-page__section">
              <h2>How to control and delete cookies</h2>
              <p>
                Most browsers allow you to control cookies through their settings. If you disable or delete cookies,
                some parts of the website may not function properly.
              </p>
              <p>You can also opt out of many third-party cookies via:</p>
              <ul className="legal-page__list">
                <li>
                  <a href="https://www.youronlinechoices.com/" target="_blank" rel="noreferrer">
                    https://www.youronlinechoices.com/
                  </a>
                </li>
                <li>
                  <a href="https://optout.aboutads.info/" target="_blank" rel="noreferrer">
                    https://optout.aboutads.info/
                  </a>
                </li>
                <li>
                  <a href="https://optout.networkadvertising.org/" target="_blank" rel="noreferrer">
                    https://optout.networkadvertising.org/
                  </a>
                </li>
              </ul>
            </div>

            <div className="legal-page__section">
              <h2>Do we sell your personal data?</h2>
              <p>No. Phennx does not sell your personal information to third parties.</p>
            </div>

            <div className="legal-page__section">
              <h2>Updates to this Cookie Policy</h2>
              <p>
                We may update this Cookie Policy from time to time to reflect changes in our practices or applicable
                laws. We will post the revised policy with a new “Last updated” date.
              </p>
            </div>

            <div className="legal-page__section">
              <h2>Contact</h2>
              <p>If you have any questions about this Cookie Policy, contact us at info@phennx.io.</p>
            </div>
          </div>
        </section>
      </article>
    </>
  );
}
